import React, {useEffect} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {History} from 'history';
import {FieldRenderProps, useField, useForm} from 'react-final-form-hooks';
import format from 'date-fns/format';
import wrap from 'wraptile';
import {curry} from 'ramda';
import CircularProgress from '@material-ui/core/CircularProgress';

import handleUnmount from '@pluto/handle-unmount';
import {UserOption} from '@pluto/users';
import {UseParamsId} from '@pluto/@types';
import makeRedirectInterval from '@pluto/makeRedirectInterval';
import {Notification} from '@pluto/notification';
import UserDetails from './UserDetails';
import {
    onHistoryRoutePush,
    UserFieldValues,
    ErrorMessages,
    getErrorMessage,
} from '../UserAdd/UserAddContainer';
import {adminRoutes} from '../../App/AppRouter/routes';

interface InitialValues extends Partial<UserFieldValues> {
    status: string;
    roles: string[];
}

interface ValidationValues {
    roles: string[];
}

interface EditUserParams {
    id: number;
    body: string[];
}

export interface UpdateHandlerParams {
    editUser: (params: EditUserParams) => void;
    formValues: string[],
    id: number;
    history: History;
    notification: any;
}

interface UserDetailsContainerProps {
    setUser: (id: number) => void;
    resetUser: () => void;
    editUser: (params: EditUserParams) => void;
    userDetails: UserOption;
    notification: Notification;
}

const onSubmit = () => {};

export default function UserDetailsContainer(props: UserDetailsContainerProps) {
    const history = useHistory();
    const {id}: UseParamsId = useParams();
    const {
        setUser,
        resetUser,
        editUser,
        userDetails,
        notification,
    } = props;
    const {
        status,
        roles,
    } = userDetails;
    const initialValues: InitialValues = {
        status,
        roles,
    };
    
    const {
        form,
        valid,
        errors,
    } = useForm({
        onSubmit,
        validate,
        initialValues,
    });
    
    const formValues = form.getState().values;
    
    const onUpdateHandler = updateHandler({
        editUser,
        formValues,
        id,
        history,
        notification,
    });
    
    const fetchUser = onEffect(setUser, id);
    useEffect(fetchUser, [
        setUser,
        id,
    ]);
    
    const rolesField = useField('roles', form);
    const statusesField = useField('status', form);
    
    const roleErrorMessage = getErrorMessage(errors, ErrorMessages.roleErrorMessage);
    const pushRouteToHistory = onHistoryRoutePush(
        history,
    );
    
    const onUnmount = handleUnmount(resetUser);
    useEffect(onUnmount, []);
    
    if (!userDetails.status) {
        return <CircularProgress />;
    }
    
    return (
        <UserDetails
            userDetails={userDetails}
            rolesField={rolesField}
            statusesField={statusesField}
            valid={valid}
            roleErrorMessage={roleErrorMessage}
            pushRouteToHistory={pushRouteToHistory}
            onChangeRole={onChangeRole}
            onChangeStatus={onChangeStatus}
            onUpdateHandler={onUpdateHandler}
            formatDate={formatDate}
        />
    );
}

export const formatDate = (date: string) => {
    return format(new Date(date), 'MM/dd/yyyy HH:MM a');
};

const returnToUsersList = (notification: Notification, history: History) => {
    makeRedirectInterval(notification, history, adminRoutes.users);
};

export const updateHandler = wrap((params: UpdateHandlerParams) => {
    const {
        editUser,
        formValues,
        id,
        history,
        notification,
    } = params;
    
    editUser({
        id,
        body: formValues,
    });
    
    returnToUsersList(notification, history);
});

export const validate = (values: ValidationValues) => {
    const {roles} = values;
    const roleErrorText = [ErrorMessages.roleErrorMessage];
    
    if (!roles.includes('admin') && !roles.includes('qcOperator') && !roles.includes('contentOpsCoordinator')) {
        return roleErrorText;
    }
    
    return [];
};

const onEffect = wrap((setUser: (id: number) => void, id: number) => {
    setUser(id);
});

export const getRoleFilter = curry((role, item) => {
    return item !== role;
});

export const onChangeStatus = wrap((statusField: FieldRenderProps, status: string) => {
    const {input} = statusField;
    const {
        value,
        onChange,
    } = input;
    
    if (value !== status)
        onChange(status);
});

export const onChangeRole = wrap((roleField: FieldRenderProps, role: string) => {
    const {input} = roleField;
    const {
        value,
        onChange,
    } = input;
    
    if (value.includes(role)) {
        onChange(value.filter(getRoleFilter(role)));
    } else {
        onChange([...value, role]);
    }
});
